import {APP_INITIALIZER, NgModule, provideZoneChangeDetection} from '@angular/core';
import {CookieBannerComponent} from './common/consent/cookie-banner.component';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClientJsonpModule, HttpClientModule} from '@angular/common/http';
import {environment} from '../environments/environment';
import {FirebaseApi} from './common/services/firebase-api.service';
import {AuthService} from './common/services/auth.service';
import {LoginModule} from './login/login.module';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {AuthGuardService} from './common/services/auth-guard.service';
import {LoginGuardService} from './common/services/login-guard.service';
import './common/utils/common.util';
import {ProjectService} from './canvas/services/project.service';
import {GoogleAnalyticsService} from './common/services/google-analytics.service';
import {StringsService} from '../../projects/common/src/lib/services/strings.service';
import {AppIconsService, appIconsFactory} from './common/services/app-icons.service';
import {Logger} from './common/utils/logger';
import {UnathorizedPage} from './core/pages/403-unauthorized.page';
import {ImpersonatePage} from './core/pages/impersonate.page';
import {NotFoundPage} from './core/pages/not-found.page';
import {GuestStartPage} from './core/pages/guest-start.page';
import {EmbedUnavailablePage} from './core/pages/embed-unavailable.page';
import {EmailRevertPage} from './core/pages/email-revert.page';
import {UnsubscribePage} from './core/pages/unsubscribe.page';
import {MatIconModule} from '@angular/material/icon';
import {SolarSystemPage} from './core/pages/solar-system.page';
import {CanvasAuthGuardService} from './common/services/canvas-auth-guard.service';
import {ServiceWorkerModule} from '@angular/service-worker';
import {UpdateService} from './common/services/update.service';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {LogService} from './common/services/log.service';
import {IframeApiGuardService} from './common/services/iframe-api-guard.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {AuthApiService} from './common/services/api/routes/auth-api.service';
import {DeveloperApiService} from './common/services/api/routes/developer-api.service';
import {InvoiceApiService} from './common/services/api/routes/invoice-api.service';
import {ProjectApiService} from './common/services/api/routes/project-api.service';
import {SubscriptionApiService} from './common/services/api/routes/subscription-api.service';
import {SupportApiService} from './common/services/api/routes/support-api.service';
import {AngularFireAuthGuard} from '@angular/fire/compat/auth-guard';
import {AngularFireModule} from '@angular/fire/compat';
import {AngularFireAuthModule} from '@angular/fire/compat/auth';
import {AngularFireDatabaseModule} from '@angular/fire/compat/database';
import {AngularFirestoreModule} from '@angular/fire/compat/firestore';
import {AngularFireStorageModule} from '@angular/fire/compat/storage';
import {GoogleMapsModule} from '@angular/google-maps';
import {UserApiService} from './common/services/api/routes/user-api.service';
import {SignoutComponent} from './common/components/signout.component';
import {provideUiTour} from 'ngx-ui-tour-md-menu';
import {NotificationsApiService} from './common/services/api/routes/notifications-api.service';
import {NotificationsService} from './common/services/notifications.service';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {HighlightModule, HIGHLIGHT_OPTIONS} from 'ngx-highlightjs';
import {MediaMatcher} from '@angular/cdk/layout';
import {AppMediaMatcher} from './common/utils/app-media-matcher';

export function authServiceFactory(service: AuthService) {
  return () => service.load();
}

export function stringsFactory(stringsService: StringsService) {
  Logger.log('stringsFactory');
  return () => stringsService.load();
}

@NgModule({
  declarations: [
    AppComponent,
    SignoutComponent,

    // Pages
    NotFoundPage,
    GuestStartPage,
    EmbedUnavailablePage,
    EmailRevertPage,
    UnsubscribePage,
    UnathorizedPage,
    ImpersonatePage,
    SolarSystemPage
  ],
  imports: [
    CookieBannerComponent,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    AngularFirestoreModule,
    AngularFireStorageModule,
    GoogleMapsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CommonModule,
    LoginModule,
    HttpClientModule,
    HttpClientJsonpModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    NgOptimizedImage,
    ServiceWorkerModule.register('ngsw-worker.js', {enabled: environment.production}),
    HighlightModule
  ],
  providers: [
    // Angular 22 no longer enables zone-based change detection by default —
    // without this the app runs on a NoopNgZone and NOTHING refreshes
    // automatically (the canvas panels/map only repainted via manual
    // detectChanges calls). This restores the automatic v14-style behavior:
    // any DOM event / timer / promise triggers a global CD tick via zone.js.
    provideZoneChangeDetection(),
    // Material animates even under OS reduced motion; the app tones motion
    // down itself (styles/_motion.scss). See AppMediaMatcher.
    {provide: MediaMatcher, useClass: AppMediaMatcher},
    FirebaseApi,
    AuthApiService,
    DeveloperApiService,
    InvoiceApiService,
    ProjectApiService,
    UserApiService,
    SubscriptionApiService,
    SupportApiService,
    ProjectService,
    GoogleAnalyticsService,
    AuthGuardService,
    IframeApiGuardService,
    CanvasAuthGuardService,
    LoginGuardService,
    DeviceDetectorService,
    AngularFireAuthGuard,
    UpdateService,
    NotificationsService,
    NotificationsApiService,
    {provide: APP_INITIALIZER, useFactory: authServiceFactory, deps: [AuthService], multi: true},
    {provide: APP_INITIALIZER, useFactory: stringsFactory, deps: [StringsService], multi: true},
    {provide: APP_INITIALIZER, useFactory: appIconsFactory, deps: [AppIconsService], multi: true},
    provideUiTour(),
    LogService,
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
        languages: {
          json: () => import('highlight.js/lib/languages/json'),
          xml: () => import('highlight.js/lib/languages/xml'),
          javascript: () => import('highlight.js/lib/languages/javascript'),
          typescript: () => import('highlight.js/lib/languages/typescript'),
        }
      }
    }
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {
}
